// -----------------------------------------------------------------------------
// checklistOutcome — shared helpers for the completed-treatment hub's counts and
// paused state. Pure (no React / redux) so the hook, CompletionChecklist and the
// hub all derive the same numbers.
//
// The hook's `done` bucket means "out of the active queue" and deliberately holds
// completed + skipped + paused items. These helpers split that bucket so the UI
// never shows a paused/skipped item as "Done". NB: `completed_at` is NOT a
// reliable completion signal — the BE sets it on paused items too.
// -----------------------------------------------------------------------------
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import { parseApiDate } from 'src/utils/taskDateUtils';

/** 'paused' | 'skipped' | 'done' for an item in the done bucket. */
export function getItemOutcome(item) {
    if (!item) return null;
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED) return 'paused';
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.SKIPPED) return 'skipped';
    return 'done';
}

/** Counts across the grouped buckets. total = every scoped item. */
export function countChecklistOutcomes(groups) {
    const { today = [], later = [], done = [] } = groups || {};
    const counts = { done: 0, skipped: 0, paused: 0, total: today.length + later.length + done.length };
    done.forEach((item) => {
        counts[getItemOutcome(item)] += 1;
    });
    return counts;
}

/**
 * "Paused until <date>" label — same format as AttendedGuidedChecklistV2
 * ('dddd D MMMM'). null when the date is missing/invalid (caller falls back to
 * 'Checklist paused'). paused_until may be DRF "DD-MM-YYYY HH:mm:ss.SSSSSSZ" or ISO.
 */
export function formatPausedUntil(raw) {
    const d = raw ? parseApiDate(raw) : null;
    return d && d.isValid() ? d.format('dddd D MMMM') : null;
}
